/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { AxiosResponse, AxiosError } from 'axios';
import { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import * as qs from 'query-string';
import { defaultTo, identity } from 'lodash';
import { FORM_ERROR } from 'final-form';

import { getConfig } from 'id-utils/config';
import { resetToMFASetupChallenge } from 'id-api/auth';
import { WORKSPACE_SUPPORT_EMAIL, WORKSPACE_SUPPORT_PHONE } from 'id-constants/global';

import { cookies, DeserializeBase64EncodedJSONCookie, getCookieValue } from './cookies';

const LOGIN_METHODS = {
  TPR: 'tprSAML',
  BLEND: 'blendSAML',
};

const LOGIN_METHOD_TO_URL = {
  [LOGIN_METHODS.TPR]: '/auth/saml/tpr',
  [LOGIN_METHODS.BLEND]: '/auth/saml/blend',
};

export interface ChallengeCookieHook {
  cookie: Identity.API.ChallengeCookie | null;
  type: 'success' | 'failure';
}

const failedCookieExtraction: ChallengeCookieHook = { cookie: null, type: 'failure' };

export const isWorkspaceUser = (clientAppIdFromCookie: string): boolean => {
  return clientAppIdFromCookie.startsWith('blend-bridge');
};

// once user submits a "task" in the login flow and the BE responds with a cookie saying "okay well based off this task, here is the next task you should be in"
// this essentially acts as an ephemeral bookmark/validator of sorts so if the flow is broken the user can reenter the app and pick up where they left off in the flow
export const useChallengeCookie = (): ChallengeCookieHook => {
  const { challengeCookieName } = getConfig();
  const challengeCookieNotExtracted = cookies.get(challengeCookieName);
  if (!challengeCookieNotExtracted || typeof challengeCookieNotExtracted !== 'string') {
    return failedCookieExtraction;
  }
  try {
    const parsedCookie = DeserializeBase64EncodedJSONCookie<Identity.API.ChallengeCookie>(
      challengeCookieNotExtracted as string, // safe due to previous check
    );
    return { cookie: parsedCookie, type: 'success' };
  } catch (error) {
    Sentry.captureException(error);
    return failedCookieExtraction;
  }
};

export const useCancelHook = (setCustomError: (value: string) => void): (() => void) => {
  const challengeCookieResult = useChallengeCookie();
  const returnToMFASetupSupported =
    challengeCookieResult.cookie && challengeCookieResult.cookie.returnToMFASetupSupported;
  const history = useHistory();

  if (returnToMFASetupSupported) {
    return async () => {
      try {
        const { data } = await resetToMFASetupChallenge();
        if (data.internalRedirect) {
          history.push(data.location);
        } else {
          window.location.assign(data.location);
        }
      } catch (err) {
        const defaultError =
          'There was a problem going back to the previous page. Please try clearing your cookies and logging in again. If the issue persists, reach out to support@blend.com. ';
        setCustomError(defaultError + (err as AxiosError).message);
      }
    };
  }

  const { challengeCookieName } = getConfig();
  return () => {
    cookies.remove(challengeCookieName, { path: '/' });
    history.push('/client/login');
  };
};

export const useSSOHooks = (): Record<keyof typeof LOGIN_METHODS, () => void> => {
  return useMemo(() => {
    return Object.entries(LOGIN_METHODS).reduce((acc, [key, method]) => {
      acc[key as keyof typeof LOGIN_METHODS] = () => {
        window.location.assign(LOGIN_METHOD_TO_URL[method]);
      };
      return acc;
    }, {} as Record<keyof typeof LOGIN_METHODS, () => void>);
  }, []);
};

export const useLoginFlowSubmit = <FormValues, PostBody>(
  submitAPI: (formValues: PostBody) => Promise<AxiosResponse<Identity.API.AuthResponse>>,
  formValuesTransformer: (formValues: FormValues) => PostBody = identity,
) => {
  const history = useHistory();
  const { clientAppIdCookieName, challengeCookieName } = getConfig();
  const clientAppIdFromCookie = getCookieValue(clientAppIdCookieName) || '';
  const defaultError =
    'There was a problem logging into your account. Please try resetting your password. If the issue persists, reach out to support@blend.com.';
  const workspaceDefaultError = `There was a problem logging into your account. Please try resetting your password. If the issue persists, reach out to ${WORKSPACE_SUPPORT_EMAIL} or call ${WORKSPACE_SUPPORT_PHONE}.`;
  const onSubmit = async (values: FormValues) => {
    try {
      const { data } = await submitAPI(formValuesTransformer(values));
      if (data.internalRedirect) {
        history.push(data.location);
      } else {
        window.location.assign(data.location);
      }
    } catch (err) {
      const responseError = (err as AxiosError<Identity.API.LoginError>).response?.data;
      // if the error is NOT actionable just surface a failed request in the UI
      if (!responseError?.type) {
        return { [FORM_ERROR]: isWorkspaceUser(clientAppIdFromCookie) ? workspaceDefaultError : defaultError };
      }

      const { generalError, inputErrors, type } = responseError;
      switch (type) {
        case 'redirectToStart': {
          cookies.remove(challengeCookieName, { path: '/' });
          return history.push({
            pathname: '/client/login',
            search: qs.stringify({ error: defaultTo(generalError, defaultError) }),
          });
        }
        case 'submitError': {
          return { [FORM_ERROR]: generalError, ...inputErrors };
        }
      }
    }
  };
  return onSubmit;
};

export const useQuery = <T>() => {
  return qs.parse(useLocation().search) as any as T;
};

export const extractValue = (param: string) => {
  const argStart = param.indexOf('=');
  return param.slice(argStart + 1);
};
